import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getAccountActionStatus, submitAccountAction } from '../api';
import Dropdown from './utils/Dropdown';

const AccountManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    action: '',
    reason: ''
  });
  const [accountAction, setAccountAction] = useState(null);
  const [status, setStatus] = useState('checking');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const ACTION_ITEMS = [
    { label: 'Select Action', value: '' },
    { label: '⏸️ Suspend Account', value: 'suspend' },
    { label: '🗑️ Delete Account', value: 'delete' }
  ];

  // ✅ Check account action status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (!user?.userId && !user?.id) {
        setStatus('error');
        setMessage('User not authenticated');
        return;
      }

      try {
        setStatus('checking');
        const userId = (user.userId || user.id).toString();
        console.log('🔍 Checking status for user:', userId);
        
        const response = await getAccountActionStatus(userId);
        
        if (response.success && response.accountActions && response.accountActions.length > 0) {
          const latestAction = response.accountActions[0];
          setAccountAction(latestAction);
          setStatus('hasAction');
          
          if (!latestAction.isAdminAction) {
            setMessage(
              `⚠️ Account ${latestAction.actionType} ${latestAction.status.toLowerCase()}. Admin review pending.`
            );
          }
        } else {
          setStatus('noAction');
        }
      } catch (error) {
        console.error('Status check error:', error);
        setStatus('noAction');
      }
    };

    checkStatus();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (message && !message.includes('in progress')) setMessage('');
  };

  const handleActionChange = (val) => {
    setFormData(prev => ({
      ...prev,
      action: val
    }));
    if (message && !message.includes('in progress')) setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (status === 'hasAction' && accountAction && !accountAction.isAdminAction) {
      setMessage('⚠️ Action already in progress. Please wait for admin review.');
      return;
    }

    if (!formData.action) {
      setMessage(' Please select an action');
      return;
    }
    if (!formData.reason.trim()) {
      setMessage(' Please provide a reason');
      return;
    }
    if (formData.reason.trim().length < 10) {
      setMessage('⚠️ Reason must be at least 10 characters');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await submitAccountAction({
        action: formData.action,
        reason: formData.reason
      });

      if (response.success) {
        setMessage(
          `✅ ${response.message || 'Account action request submitted successfully. Awaiting admin review.'}`
        );
        setFormData({ action: '', reason: '' });
        setStatus('hasAction');
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setMessage(` ${response.message || 'Something went wrong'}`);
      }
    } catch (error) {
      setMessage(` ${error.message || 'Failed to process request. Please try again.'}`);
      console.error('Submit error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // ✅ Show status table if action exists
  if (status === 'hasAction' && accountAction) {
    return (
      <div className="auth-container">
        <div className="auth-main-container account-action-page">
          <div className="auth-header">
            <h1 className="auth-title">Account Action Status</h1>
            <p className="auth-subtitle">Current account action details</p>
          </div>

          {/* Status Table */}
          <div className="status-table-container">
            <table className="account-action-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>#{accountAction.accountActionId}</strong></td>
                  <td>
                    <span className={`action-badge ${accountAction.actionType?.toLowerCase()}`}>
                      {accountAction.actionType === 'suspend' ? '⏸️ Suspend' : '🗑️ Delete'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${accountAction.status?.toLowerCase()}`}>
                      {accountAction.status}
                    </span>
                  </td>
                  <td>{accountAction.isAdminAction ? '👨‍💼 Admin' : '👤 User'}</td>
                  <td className="text-muted">
                    {new Date(accountAction.createdDate).toLocaleDateString()}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Reason Box */}
            <div className="action-details-box">
              <strong>Reason:</strong>
              <p>{accountAction.reason}</p>
            </div>
          </div>

          {!accountAction.isAdminAction && (
            <div className="form-info warning">
              <strong>⚠️ Status:</strong> Account {accountAction.actionType} request{' '}
              <strong>{accountAction.status.toLowerCase()}</strong>. Admin review pending.{' '}
              You cannot submit new actions until this is resolved.
            </div>
          )}

          <div className="modal-buttons">
            <button onClick={handleCancel} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Loading state
  if (status === 'checking') {
    return (
      <div className="auth-container">
        <div className="auth-main-container">
          <div className="auth-header">
            <div className="auth-logo loading">⏳</div>
            <h1 className="auth-title">Checking Account Status...</h1>
            <p className="auth-subtitle">Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (status === 'error') {
    return (
      <div className="auth-container">
        <div className="auth-main-container">
          <div className="auth-header">
            <div className="auth-logo error"></div>
            <h1 className="auth-title">Error</h1>
            <p className="auth-subtitle">{message || 'Failed to load account status'}</p>
            <div className="modal-buttons">
              <button onClick={handleCancel} className="btn-primary">Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Show form (no action or admin action exists)
  return (
    <div className="auth-container">
      <div className="auth-main-container account-form">
        <div className="auth-header">
          <h1 className="auth-title">Account Management</h1>
          <p className="auth-subtitle">
            {status === 'noAction' 
              ? 'No pending actions found. Submit new request below.' 
              : 'Admin action detected. You can submit new user requests.'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="">
          <div className="form-field">
            <label className="form-label">
              Action <span className="required">*</span>
            </label>
            <Dropdown
              items={ACTION_ITEMS}
              value={formData.action}
              onChange={handleActionChange}
              placeholder="Select Action"
              buttonClass="form-dropdown-container"
              menuClass="form-dropdown-menu"
              itemClass="form-dropdown-item"
              disabled={isLoading}
            />
          </div>

          <div className="form-field">
            <label className="form-label">
              Reason <span className="required">*</span>
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              placeholder="Please provide a detailed reason for this action (minimum 10 characters)..."
              className="form-textarea"
              rows="6"
              disabled={isLoading}
              maxLength={500}
            />
            <div className="char-count">
              {formData.reason.length}/500 characters
            </div>
          </div>

          {message && (
            <div className={`alert ${
              message.includes('') ? 'alert-error' :
              message.includes('⚠️') ? 'alert-warning' : 'alert-success'
            }`}>
              <span className="alert-icon">
                {message.includes('') ? '' : message.includes('⚠️') ? '⚠️' : '✅'}
              </span>
              {message}
            </div>
          )}

          {formData.action && (
            <div className="warning-notice">
              <div className="warning-icon">⚠️</div>
              <div className="warning-content">
                <strong>Warning:</strong>{' '}
                {formData.action === 'delete'
                  ? 'This action is permanent and cannot be undone. The account and its associated data will be scheduled for deletion within 30 days.'
                  : 'This action will temporarily restrict account access. The account may remain suspended for up to 30 days unless manually reactivated.'}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || !formData.action || !formData.reason.trim()}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Processing...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountManagementPage;
